// Adapted from davinci-elections packages/shared/src/api/common.ts @ 6e23f58
import { z } from 'zod'

/** Stable machine-readable error codes (`ApiErrorBody.error.code`). */
export const API_ERROR_CODES = [
  'bad_request',
  'validation_error',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'payload_too_large',
  'internal',
  'unavailable',
  'invalid_state_transition',
  'invalid_registration',
  'invalid_code',
  'code_expired',
  'code_attempts_exceeded',
  'resend_too_soon',
  'invalid_credentials',
  'invalid_challenge',
  'invalid_link',
  'fresh_sign_in_required', // passwordless §4, P2: a change that needs a sign-in within the last 10 minutes
  'approval_needs_recapture',
  'uniqueness_conflict',
  'needs_confirmation',
  'provider_not_active',
  'provider_not_approved',
  'last_provider',
  'capture_refused',
  'attempt_limit',
  'attempt_open',
  'credentials_refused',
  'invalid_signature',
  'matcher_unavailable',
  'try_later',
  'relay_refused', // T23: relay of an attempt that is not pending, past its deadline (E12.4)
  'test_provider', // T23: a test provider cannot be the default (E12.9)
  'device_location_required', // location v2: S3 / the first face start without the device location the policy needs
  'search_too_broad', // identity document §13: an admin search that would decrypt too many rows (add a filter)
  // identity document §3.2, §6.1, §7 (Task 14): document policy and catalogue refusals
  'document_provider_not_selectable',
  'document_provider_no_coverage',
  'document_country_off',
  'document_catalogue_static',
  'document_catalogue_failed',
  // identity document §4.2 (Task 19): document attempt start refusals
  'document_face_check_running',
  'document_not_needed',
  'document_manual_only',
  'document_attempt_limit',
  // identity document §4.5 (Task 21): a face start while the requirement applies and no evidence unit is current
  'document_required',
  // identity document §10.1 (gate P3 A2): the start's consent named another provider or text version than now
  'document_consent_changed',
  // face-first §3.4 B: the exempt Continue found the exemption no longer standing (the step is re-evaluated)
  'document_exemption_stale',
  // identity document §5.2 (Task 28): a face start or new face check of a member whose anchor is missing
  'manual_route',
  // identity-document §13 (gate P4 A3): an approval refused by the document / anchor rules of the shared approval
  'approval_needs_manual_verification',
  'needs_document_confirmation',
  // identity-document §10.3, §13 (Task 30): "Verified manually" on a review with a template and nothing to resolve
  'manual_verification_not_needed',
] as const
export const ApiErrorCodeSchema = z.enum(API_ERROR_CODES)
export type ApiErrorCode = z.infer<typeof ApiErrorCodeSchema>

export const ApiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    /** For validation_error: zod `issues` (path + message); otherwise free-form. */
    details: z.unknown().optional(),
  }),
})
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>

export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>

export const paginated = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
  })
